// CreaturesByLocationView.jsx - Mobile-optimized creatures organized by dive site location
import React, { useState, useMemo } from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  BugReport as CreatureIcon,
  ExpandMore as ExpandIcon,
  Visibility as SightingIcon,
  TrendingUp as TrendingIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useUnits } from "../../contexts/UnitContext";

const CreaturesByLocationView = ({
  creaturesByLocation = [],
  divesites = [],
  onRefresh,
}) => {
  const theme = useTheme();
  const { convert } = useUnits();
  const [expandedLocation, setExpandedLocation] = useState(null);

  // Mobile breakpoint detection
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXsScreen = useMediaQuery(theme.breakpoints.down("xs"));

  // Get color for creature category
  const getCategoryColor = (category) => {
    const colors = {
      fish: theme.palette.info.main,
      invertebrate: theme.palette.warning.main,
      mammal: theme.palette.success.main,
      reptile: theme.palette.error.main,
      plant: theme.palette.primary.main,
      other: theme.palette.grey[500],
    };
    return colors[category?.toLowerCase()] || colors.other;
  };

  // Process and sort locations by creature activity
  const processedLocations = useMemo(() => {
    return creaturesByLocation
      .map((location) => ({
        ...location,
        totalCreatures: location.creatures?.length || 0,
        totalSightings:
          location.creatures?.reduce(
            (sum, c) => sum + (c.sightingCount || 0),
            0
          ) || 0,
        categories:
          [
            ...new Set(
              location.creatures?.map((c) => c.category).filter(Boolean)
            ),
          ] || [],
        recentActivity:
          location.creatures?.some((c) => {
            const lastSeen = new Date(c.lastSeen);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return lastSeen > thirtyDaysAgo;
          }) || false,
      }))
      .sort((a, b) => b.totalSightings - a.totalSightings);
  }, [creaturesByLocation]);

  const handleAccordionChange = (locationId) => (event, isExpanded) => {
    setExpandedLocation(isExpanded ? locationId : null);
  };

  // Truncate text for mobile
  const truncateText = (text, maxLength = 20) => {
    if (!isMobile || !text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Format date for mobile
  const formatDate = (dateString) => {
    if (isMobile) {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
    }
    return convert.time.formatDate(new Date(dateString));
  };

  if (processedLocations.length === 0) {
    return (
      <Paper
        sx={{
          p: isMobile ? 3 : 4,
          textAlign: "center",
          borderRadius: 2,
          mx: isMobile ? 1 : 0,
        }}
      >
        <LocationIcon
          sx={{
            fontSize: isMobile ? 48 : 64,
            color: "text.secondary",
            mb: isMobile ? 1.5 : 2,
          }}
        />
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          color="textSecondary"
          gutterBottom
        >
          No location data available
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
        >
          Add some divesites to your dashboard and log creatures to see them
          here
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ px: isMobile ? 1 : 0 }}>
      {/* Location Overview Cards */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        {processedLocations
          .slice(0, isMobile ? 2 : 3)
          .map((location, index) => (
            <Grid item key={location._id || index} xs={12} sm={6} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  border:
                    index === 0
                      ? `2px solid ${theme.palette.primary.main}`
                      : "none",
                  position: "relative",
                  // Reduce margins on mobile
                  mx: isMobile ? 0 : "auto",
                }}
              >
                {index === 0 && (
                  <Chip
                    label={isMobile ? "Top" : "Most Active"}
                    color="primary"
                    size="small"
                    icon={
                      <TrendingIcon fontSize={isMobile ? "small" : "medium"} />
                    }
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      fontSize: isMobile ? "0.65rem" : "0.75rem",
                      height: isMobile ? 24 : 32,
                    }}
                    clickable={false}
                    onClick={() => {}}
                  />
                )}

                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        width: isMobile ? 40 : 48,
                        height: isMobile ? 40 : 48,
                      }}
                    >
                      <LocationIcon fontSize={isMobile ? "small" : "medium"} />
                    </Avatar>
                  }
                  title={
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      fontWeight="bold"
                      sx={{
                        // Allow wrapping on mobile
                        whiteSpace: isMobile ? "normal" : "nowrap",
                        overflow: "visible",
                        textOverflow: "unset",
                        lineHeight: isMobile ? 1.2 : 1.6,
                      }}
                    >
                      {isMobile
                        ? truncateText(location.name, 25)
                        : location.name}
                    </Typography>
                  }
                  subheader={
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        whiteSpace: isMobile ? "normal" : "nowrap",
                      }}
                      title={`${location.city}, ${location.province}`}
                    >
                      {isMobile
                        ? truncateText(
                            `${location.city}, ${location.province}`,
                            30
                          )
                        : `${location.city}, ${location.province}`}
                    </Typography>
                  }
                  sx={{
                    pb: isMobile ? 1 : 2,
                    "& .MuiCardHeader-content": {
                      minWidth: 0,
                    },
                  }}
                />

                <CardContent sx={{ pt: 0, px: isMobile ? 2 : 3 }}>
                  <Stack
                    direction="row"
                    spacing={isMobile ? 1 : 2}
                    sx={{ mb: isMobile ? 1.5 : 2 }}
                  >
                    <Box sx={{ textAlign: "center", flex: 1 }}>
                      <Typography
                        variant={isMobile ? "h5" : "h4"}
                        fontWeight="bold"
                        color="primary"
                      >
                        {location.totalCreatures}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
                      >
                        Species
                      </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box sx={{ textAlign: "center", flex: 1 }}>
                      <Typography
                        variant={isMobile ? "h5" : "h4"}
                        fontWeight="bold"
                        color="secondary"
                      >
                        {location.totalSightings}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
                      >
                        Sightings
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Categories */}
                  {location.categories.length > 0 && (
                    <Box sx={{ mb: isMobile ? 1 : 2 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                      >
                        Categories Found
                      </Typography>
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        spacing={isMobile ? 0.5 : 0.5}
                        sx={{
                          flexWrap: isMobile ? "nowrap" : "wrap",
                          gap: isMobile ? 0.5 : 0.5,
                          maxHeight: isMobile ? 100 : "auto",
                          overflowY: isMobile ? "auto" : "visible",
                        }}
                      >
                        {location.categories
                          .slice(0, isMobile ? 4 : 6)
                          .map((category, idx) => (
                            <Chip
                              key={idx}
                              label={category}
                              size="small"
                              sx={{
                                bgcolor: getCategoryColor(category),
                                color: "white",
                                fontSize: isMobile ? "0.65rem" : "0.7rem",
                                height: isMobile ? 24 : 32,
                                alignSelf: isMobile ? "flex-start" : "auto",
                              }}
                              clickable={false}
                              onClick={() => {}}
                            />
                          ))}
                        {location.categories.length > (isMobile ? 4 : 6) && (
                          <Chip
                            label={`+${
                              location.categories.length - (isMobile ? 4 : 6)
                            }`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: isMobile ? "0.65rem" : "0.7rem",
                              height: isMobile ? 24 : 32,
                              alignSelf: isMobile ? "flex-start" : "auto",
                            }}
                            clickable={false}
                            onClick={() => {}}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Recent Activity Indicator */}
                  {location.recentActivity && (
                    <Box
                      sx={{
                        mt: isMobile ? 1 : 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 6 : 8,
                          height: isMobile ? 6 : 8,
                          borderRadius: "50%",
                          bgcolor: theme.palette.success.main,
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight="medium"
                        sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
                      >
                        Recent Activity
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Detailed Location Breakdown */}
      <Typography
        variant={isMobile ? "h6" : "h5"}
        gutterBottom
        sx={{
          mb: isMobile ? 2 : 3,
          fontWeight: "bold",
          px: isMobile ? 1 : 0,
        }}
      >
        Detailed Breakdown
      </Typography>

      {processedLocations.map((location, index) => (
        <Accordion
          key={location._id || index}
          expanded={expandedLocation === location._id}
          onChange={handleAccordionChange(location._id)}
          sx={{
            mb: isMobile ? 1.5 : 2,
            borderRadius: 2,
            "&:before": { display: "none" },
            mx: isMobile ? 0.5 : 0,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandIcon />}
            sx={{
              bgcolor: theme.palette.action.hover,
              borderRadius: 2,
              minHeight: isMobile ? 56 : 48, // Larger touch target on mobile
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                minHeight: isMobile ? 56 : 48,
              },
              "& .MuiAccordionSummary-content": {
                margin: isMobile ? "12px 0" : "12px 0",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  mr: isMobile ? 1.5 : 2,
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                }}
              >
                <LocationIcon fontSize={isMobile ? "small" : "medium"} />
              </Avatar>

              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight="bold"
                  sx={{
                    whiteSpace: isMobile ? "nowrap" : "normal",
                    overflow: isMobile ? "hidden" : "visible",
                    textOverflow: isMobile ? "ellipsis" : "unset",
                  }}
                  title={location.name}
                >
                  {isMobile ? truncateText(location.name, 25) : location.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    whiteSpace: isMobile ? "nowrap" : "normal",
                    overflow: isMobile ? "hidden" : "visible",
                    textOverflow: isMobile ? "ellipsis" : "unset",
                  }}
                  title={`${location.city}, ${location.province}`}
                >
                  {isMobile
                    ? truncateText(`${location.city}, ${location.province}`, 30)
                    : `${location.city}, ${location.province}`}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={isMobile ? 2 : 3}
                sx={{
                  mr: isMobile ? 0 : 2,
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "space-around" : "flex-end",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight="bold"
                    color="primary"
                  >
                    {location.totalCreatures}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
                  >
                    Species
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight="bold"
                    color="secondary"
                  >
                    {location.totalSightings}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? "0.65rem" : "0.75rem" }}
                  >
                    Sightings
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <List sx={{ py: 0 }}>
              {location.creatures?.map((creature, idx) => (
                <React.Fragment key={creature._id || idx}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: isMobile ? 1.5 : 2,
                      px: isMobile ? 2 : 3,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: getCategoryColor(creature.category),
                          width: isMobile ? 28 : 32,
                          height: isMobile ? 28 : 32,
                        }}
                      >
                        <CreatureIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: isMobile ? "flex-start" : "center",
                            gap: 1,
                            flexDirection: isMobile ? "column" : "row",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{
                              fontSize: isMobile ? "0.9rem" : "1rem",
                              lineHeight: isMobile ? 1.2 : 1.5,
                            }}
                          >
                            {isMobile
                              ? truncateText(
                                  creature.commonName || "Unknown Creature",
                                  25
                                )
                              : creature.commonName || "Unknown Creature"}
                          </Typography>
                          <Chip
                            label={creature.category || "Other"}
                            size="small"
                            sx={{
                              bgcolor: getCategoryColor(creature.category),
                              color: "white",
                              fontWeight: "medium",
                              fontSize: isMobile ? "0.6rem" : "0.7rem",
                              height: isMobile ? 20 : 24,
                              alignSelf: isMobile ? "flex-start" : "auto",
                            }}
                            clickable={false}
                            onClick={() => {}}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography
                            sx={{ display: "inline" }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                            fontSize={isMobile ? "0.8rem" : "0.875rem"}
                          >
                            {isMobile
                              ? truncateText(
                                  creature.scientificName || "Species unknown",
                                  30
                                )
                              : creature.scientificName || "Species unknown"}
                          </Typography>
                          {creature.lastSeen && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{
                                fontSize: isMobile ? "0.65rem" : "0.75rem",
                                mt: isMobile ? 0.5 : 0,
                              }}
                            >
                              Last seen: {formatDate(creature.lastSeen)}
                              <br />({creature.sightingCount} sighting
                              {creature.sightingCount !== 1 ? "s" : ""})
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {idx < (location.creatures?.length || 0) - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default React.memo(CreaturesByLocationView);
